// OpenAI API utilities for Trades Consultant feature
// Supports dynamic prompts from Firestore with fallbacks and usage tracking

import { getPromptDefinition } from '../data/aiPrompts';
import { cleanJsonResponse, extractActionBlock, detectTruncation, validateParseResult, estimateTokens } from './openaiHelpers';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

const DEFAULT_MODEL = 'gpt-4o-mini';
const VISION_MODEL = 'gpt-4o';

// Reference to PromptContext functions - set by initOpenAI
let promptContextRef = null;

/**
 * Initialize OpenAI utils with PromptContext reference
 * Call this from components that use PromptContext
 */
export const initOpenAI = (promptContext) => {
  promptContextRef = promptContext;
};

/**
 * Call OpenAI Chat Completions API with optional tracking
 * @param {Array<Object>} messages - Chat messages
 * @param {Object} options - Additional options
 * @returns {Promise<string>} - Response text
 */
const callOpenAI = async (messages, options = {}) => {
  const {
    model = DEFAULT_MODEL,
    maxTokens = 1000,
    temperature = 0.7,
    promptId = null,
    contextId = null,
    userId = null,
  } = options;

  const startTime = Date.now();
  let success = true;
  let errorMessage = null;

  try {
    const requestBody = {
      model,
      messages,
      max_tokens: maxTokens,
      temperature,
    };

    // Debug: Log request structure (not full base64 images)
    const { totalChars, estimatedTokens: estimatedTokenCount } = estimateTokens(messages);

    console.log('[OpenAI] Request:', {
      model,
      maxTokens,
      messageCount: messages.length,
      totalChars,
      estimatedTokens: estimatedTokenCount,
      messages: messages.map(m => ({
        role: m.role,
        contentType: typeof m.content,
        contentLength: typeof m.content === 'string' ? m.content.length : m.content?.length,
        contentItems: Array.isArray(m.content) ? m.content.map(c => ({ type: c.type, hasUrl: !!c.image_url?.url })) : null,
      })),
    });

    // SAFEGUARD: Warn if estimated tokens are too high
    if (estimatedTokenCount > 100000) {
      console.error(`[OpenAI] DANGER: Estimated ${estimatedTokenCount} tokens - likely to exceed model limit!`);
    }

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      success = false;
      errorMessage = error.error?.message || `API error: ${response.status}`;

      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a moment.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    const finishReason = data.choices?.[0]?.finish_reason;
    console.log('[OpenAI] API Response:', {
      model: data.model,
      finishReason,
      usage: data.usage,
    });
    const content = data.choices[0]?.message?.content || '';

    // CRITICAL: Detect truncation - finish_reason "length" means output was cut off
    if (finishReason === 'length') {
      console.error('[OpenAI] TRUNCATION DETECTED - response hit token limit');
    }

    // Log usage if prompt tracking is enabled
    if (promptId && promptContextRef?.logUsage) {
      const latencyMs = Date.now() - startTime;
      promptContextRef.logUsage(promptId, {
        contextId,
        userId,
        latencyMs,
        success: true,
        errorMessage: null,
        inputTokens: data.usage?.prompt_tokens || 0,
        outputTokens: data.usage?.completion_tokens || 0,
      }).catch(console.error);
    }

    // Return content with metadata for truncation detection
    return {
      content,
      finishReason,
      usage: data.usage,
      truncated: finishReason === 'length',
    };
  } catch (error) {
    console.error('OpenAI API error:', error);

    // Log failure
    if (promptId && promptContextRef?.logUsage) {
      promptContextRef.logUsage(promptId, {
        contextId,
        userId,
        latencyMs: Date.now() - startTime,
        success: false,
        errorMessage: error.message,
        inputTokens: 0,
        outputTokens: 0,
      }).catch(console.error);
    }

    throw error;
  }
};

/**
 * Call a prompt by ID using dynamic prompt configuration
 * Falls back to hardcoded if PromptContext not available
 */
const callPromptWithTracking = async (promptId, variables, options = {}) => {
  if (!promptContextRef) {
    console.warn(`PromptContext not initialized, using legacy call for ${promptId}`);
    return null;
  }

  try {
    const { messages, model, maxTokens, temperature, outputFormat } = promptContextRef.buildMessages(promptId, variables);

    const result = await callOpenAI(messages, {
      model,
      maxTokens,
      temperature,
      promptId,
      ...options,
    });

    // Parse JSON if expected
    if (outputFormat === 'json') {
      return JSON.parse(cleanJsonResponse(result.content));
    }

    return result.content;
  } catch (error) {
    console.error(`Error calling prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Generate a briefing for a work order
 * Passes FULL context to AI - trust AI to extract relevant patterns
 *
 * @param {Object} wo - Current work order (full object)
 * @param {Array<Object>} unitHistory - Past WOs for this unit (full objects, limited to 20)
 * @param {Array<Object>} laborNotes - Labor entries for unit history (full objects)
 * @param {Array<Object>} propertyHistory - WOs from same property, other units (full objects, limited to 50)
 * @returns {Promise<string>} - Briefing text
 */
export const generateWOBriefing = async (wo, unitHistory = [], laborNotes = [], propertyHistory = []) => {
  // Build rich context - pass full objects, let AI decide what's relevant
  const context = {
    currentWorkOrder: wo,
    unitHistory: unitHistory,
    laborNotes: laborNotes,
    propertyHistory: propertyHistory,
    summary: {
      unitWOCount: unitHistory.length,
      propertyWOCount: propertyHistory.length,
      laborNoteCount: laborNotes.length,
    },
  };

  const systemPrompt = `You are a knowledgeable maintenance consultant helping a field technician prepare for a job.

You have access to:
1. The CURRENT work order they're about to work on
2. UNIT HISTORY - past work orders for this specific unit (most relevant)
3. LABOR NOTES - technician notes from past visits to this unit
4. PROPERTY HISTORY - work orders from OTHER units at the same property (useful for building-wide patterns)

Your job is to provide an actionable briefing that helps them work smarter.`;

  const userPrompt = `Here is the full context for work order ${wo.id}:

${JSON.stringify(context, null, 2)}

Provide a brief for the tech. Be specific and reference actual data from the context:
1. Summarize the current issue
2. Highlight patterns from unit history (recurring problems, previous solutions)
3. Note relevant property-wide patterns (other units with similar issues, building-wide problems)
4. Mention which techs have experience here and any useful notes they left
5. Give 1-2 actionable recommendations based on the data

Keep it practical and conversational. Reference specific dates, techs, or details when relevant.`;

  const messages = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ];

  const result = await callOpenAI(messages, {
    model: 'gpt-4o-mini',
    maxTokens: 1500,
    promptId: 'generateWOBriefing',
    contextId: wo.id,
  });

  return result.content;
};

/**
 * Build the template variable object for an enhanced WO briefing.
 * Pure function — exported for testing.
 *
 * @param {Object} wo - Current work order
 * @param {Array} unitHistory - Past WOs for this unit
 * @param {Array} laborNotes - Labor entries for unit history
 * @param {Array} propertyHistory - WOs from same property, other units
 * @param {Object|null} intelligence - Result of getIntelligenceForBriefing(wo)
 * @returns {Object} Template variables for generateEnhancedBriefing prompt
 */
export const buildEnhancedBriefingVars = (wo, unitHistory = [], laborNotes = [], propertyHistory = [], intelligence = null) => {
  const historyText = unitHistory.length > 0
    ? unitHistory.map(h => `- ${h.createdDate || 'N/A'}: ${h.description || 'No description'} (${h.tech || 'Unknown tech'}, ${h.status || 'unknown'})`).join('\n')
    : 'No unit history available.';

  const notesText = laborNotes.length > 0
    ? laborNotes.map(n => `- ${n.techName || n.tech || 'Tech'}: ${n.description || n.notes || 'No notes'}`).join('\n')
    : 'No labor notes available.';

  return {
    woId: wo.id || '',
    description: wo.description || '',
    property: wo.property || '',
    unit: wo.unit || wo.unitId || '',
    historyText,
    notesText,
    knowledgeNotes: intelligence?.knowledgeNotes || 'No tribal knowledge available.',
    predictedParts: intelligence?.predictedParts || 'No parts data available.',
    failureIntel: intelligence?.failureIntel || 'No failure intelligence available.',
    repairConfidence: intelligence?.repairConfidence || 'No prior repair data for this unit.',
  };
};

/**
 * Generate an enhanced briefing for a work order using intelligence data.
 * Falls back to generateWOBriefing if no intelligence is available.
 *
 * @param {Object} wo - Current work order
 * @param {Array} unitHistory - Past WOs for this unit
 * @param {Array} laborNotes - Labor entries for unit history
 * @param {Array} propertyHistory - WOs from same property, other units
 * @param {Object|null} intelligence - Result of getIntelligenceForBriefing(wo)
 * @returns {Promise<string>} - Enhanced briefing text
 */
export const generateEnhancedWOBriefing = async (wo, unitHistory = [], laborNotes = [], propertyHistory = [], intelligence = null) => {
  // Fall back to original briefing if no intelligence
  if (!intelligence) {
    return generateWOBriefing(wo, unitHistory, laborNotes, propertyHistory);
  }

  const vars = buildEnhancedBriefingVars(wo, unitHistory, laborNotes, propertyHistory, intelligence);

  // Try dynamic prompt first (PromptContext)
  if (promptContextRef) {
    try {
      return await callPromptWithTracking('generateEnhancedBriefing', vars, {
        contextId: wo.id,
      });
    } catch (error) {
      console.error('Dynamic prompt failed for enhanced briefing, using legacy:', error);
    }
  }

  // Legacy fallback using canonical prompt definitions
  const def = getPromptDefinition('generateEnhancedBriefing');
  if (!def) {
    // If enhanced prompt not defined, fall back to standard briefing
    return generateWOBriefing(wo, unitHistory, laborNotes, propertyHistory);
  }

  let userContent = def.userPromptTemplate;
  for (const [key, value] of Object.entries(vars)) {
    userContent = userContent.replace(new RegExp(`{{${key}}}`, 'g'), value);
  }

  const messages = [
    { role: 'system', content: def.systemPrompt },
    { role: 'user', content: userContent },
  ];

  const result = await callOpenAI(messages, {
    model: def.model,
    maxTokens: def.maxTokens,
    temperature: def.temperature,
    promptId: 'generateEnhancedBriefing',
    contextId: wo.id,
  });

  return result.content;
};

/**
 * Analyze a photo for maintenance diagnosis
 * @param {string} imageUrl - URL of the image
 * @param {string} context - Context about the issue
 * @returns {Promise<string>} - Analysis text
 */
export const analyzePhoto = async (imageUrl, context = '') => {
  const messages = [
    {
      role: 'system',
      content: 'You are helping a maintenance tech diagnose an issue. Be practical and specific.'
    },
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: `Context: ${context || 'Maintenance issue'}\n\nWhat do you see in this image? What might be the problem and potential solutions?`
        },
        {
          type: 'image_url',
          image_url: { url: imageUrl }
        }
      ]
    }
  ];

  return callOpenAI(messages, { model: VISION_MODEL, maxTokens: 500, promptId: 'analyzePhoto' });
};

/**
 * Generate debug checklist for an issue type
 * @param {string} issueDescription - Description of the issue
 * @param {Array<Object>} unitHistory - Past WOs for context
 * @returns {Promise<string>} - Debug checklist
 */
export const generateDebugChecklist = async (issueDescription, unitHistory = []) => {
  const historyContext = unitHistory.length > 0
    ? `\nUnit History:\n${unitHistory.map(h => `- ${h.description}`).join('\n')}`
    : '';

  // Try dynamic prompt first
  if (promptContextRef) {
    try {
      return await callPromptWithTracking('generateDebugChecklist', {
        issueDescription,
        historyContext,
      });
    } catch (error) {
      console.error('Dynamic prompt failed, using legacy:', error);
    }
  }

  // Legacy fallback using canonical prompt definitions
  const def = getPromptDefinition('generateDebugChecklist');
  let userContent = def.userPromptTemplate
    .replace(/{{issueDescription}}/g, issueDescription)
    .replace(/{{historyContext}}/g, historyContext);

  const messages = [
    { role: 'system', content: def.systemPrompt },
    { role: 'user', content: userContent },
  ];

  return callOpenAI(messages, { model: def.model, maxTokens: def.maxTokens, temperature: def.temperature, promptId: 'generateDebugChecklist' });
};

/**
 * Flag work order for efficiency review
 * @param {Object} wo - Work order details
 * @param {number} hours - Hours logged
 * @param {Array<Object>} parts - Parts used
 * @param {Array<Object>} unitHistory - Past WOs for this unit
 * @returns {Promise<{flagged: boolean, reason: string, confidence: string}>}
 */
export const analyzeWOEfficiency = async (wo, hours, parts = [], unitHistory = []) => {
  const partsText = parts.length > 0
    ? parts.map(p => `${p.description}: $${p.cost}`).join(', ')
    : 'None';

  const historyText = unitHistory.length > 0
    ? unitHistory.slice(0, 5).map(h => `- ${h.description}`).join('\n')
    : 'No history';

  // Try dynamic prompt first
  if (promptContextRef) {
    try {
      return await callPromptWithTracking('analyzeWOEfficiency', {
        description: wo.description,
        hours,
        partsText,
        historyText,
      }, { contextId: wo.id });
    } catch (error) {
      console.error('Dynamic prompt failed, using legacy:', error);
    }
  }

  // Legacy fallback using canonical prompt definitions
  const def = getPromptDefinition('analyzeWOEfficiency');
  let userContent = def.userPromptTemplate
    .replace(/{{description}}/g, wo.description)
    .replace(/{{hours}}/g, String(hours))
    .replace(/{{partsText}}/g, partsText)
    .replace(/{{historyText}}/g, historyText);

  const messages = [
    { role: 'system', content: def.systemPrompt },
    { role: 'user', content: userContent },
  ];

  try {
    const { content } = await callOpenAI(messages, { model: def.model, maxTokens: def.maxTokens, temperature: def.temperature, promptId: 'analyzeWOEfficiency', contextId: wo.id });
    const result = JSON.parse(cleanJsonResponse(content));

    return {
      flagged: !!result.flagged,
      reason: result.reason || '',
      confidence: result.confidence || 'low',
    };
  } catch (error) {
    console.error('Error parsing efficiency analysis:', error);
    return { flagged: false, reason: 'Analysis failed', confidence: 'low' };
  }
};

/**
 * Parse an uploaded estimate document into structured line items
 * @param {string} documentText - Extracted text from PDF/XLS/CSV
 * @param {string} fileName - Original file name for context
 * @returns {Promise<Object>} - Parsed estimate data
 */
export const parseEstimateDocument = async (documentText, fileName = '') => {
  // Try dynamic prompt first
  if (promptContextRef) {
    try {
      return await callPromptWithTracking('parseEstimateDocument', {
        fileName,
        documentText,
      });
    } catch (error) {
      console.error('Dynamic prompt failed, using legacy:', error);
    }
  }

  // Legacy fallback using canonical prompt definitions
  const def = getPromptDefinition('parseEstimateDocument');
  let userContent = def.userPromptTemplate
    .replace(/{{fileName}}/g, fileName)
    .replace(/{{documentText}}/g, documentText);

  const messages = [
    { role: 'system', content: def.systemPrompt },
    { role: 'user', content: userContent },
  ];

  try {
    // Use 8000 tokens to handle large estimates (139+ line items)
    const result = await callOpenAI(messages, { maxTokens: 8000, promptId: 'parseEstimateDocument' });

    // CRITICAL: Check for truncation BEFORE parsing
    const truncationCheck = detectTruncation(result);
    if (truncationCheck.truncated) {
      console.error('[TRUNCATION ERROR]', truncationCheck.reason, {
        finishReason: result.finishReason,
        usage: result.usage,
      });
      throw new Error(
        `PARSING INCOMPLETE: ${truncationCheck.reason}. ` +
        `Some line items may be missing. Please try again or contact support.`
      );
    }

    const cleaned = cleanJsonResponse(result.content);

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (jsonError) {
      console.error('[JSON PARSE ERROR] Failed to parse response:', jsonError, cleaned.slice(-200));
      throw new Error(
        `PARSING FAILED: Could not parse AI response as JSON. ` +
        `This may indicate the response was truncated. Please try again.`
      );
    }

    // VALIDATION: Compare sourceLineItemCount vs actual items
    validateParseResult(parsed);

    const actualCount = parsed.lineItems?.length || 0;
    console.log(`[Parse Success] Captured ${actualCount} line items` +
      (parsed.sourceLineItemCount ? ` (document reported ${parsed.sourceLineItemCount})` : ''));

    return parsed;
  } catch (error) {
    console.error('Error parsing estimate document:', error);
    // Re-throw with original message if it's our custom error
    if (error.message.includes('PARSING')) {
      throw error;
    }
    throw new Error('Failed to parse document. Please try again.');
  }
};

/**
 * Parse an estimate document using GPT-4o vision
 * Sends PDF pages as images - AI sees actual layout, columns, formatting
 * This is the preferred method as it doesn't lose column structure
 *
 * @param {Array<{page: number, image: string}>} pageImages - Base64 images of PDF pages
 * @param {string} fileName - Original file name for context
 * @returns {Promise<Object>} - Parsed estimate data
 */
export const parseEstimateWithVision = async (pageImages, fileName = '') => {
  if (!pageImages || pageImages.length === 0) {
    throw new Error('No images provided for vision parsing');
  }

  console.log(`[Vision Parse] Processing ${pageImages.length} page(s) from ${fileName}`);

  // Build content array - IMAGES FIRST, then instructions
  // Some vision models work better when images are presented before the text prompt
  const userPrompt = `You are parsing an insurance property damage estimate (likely from Xactimate or similar software).

CRITICAL - UNDERSTAND THE TABLE STRUCTURE:
Insurance estimates have columns like: Description | Quantity | Unit | Unit Price | Labor | Material | Equipment | TOTAL
- The TOTAL or "Line Total" column is the RIGHTMOST dollar amount - this is what we want
- Do NOT use: Quantity (just a number like "1" or "150"), Unit Price (per-unit rate), or component columns (Labor/Material/Equipment)
- The TOTAL column = Quantity × Unit Price, or = Labor + Material + Equipment

COMMON PATTERNS:
- "Remove drywall - 150 SF @ $2.50/SF = $375.00" → amount is $375.00 (the total), NOT $2.50 (the rate) or 150 (the quantity)
- Equipment rentals like "Dehumidifier - 5 days @ $75/day = $375.00" → amount is $375.00, NOT $75
- If you see the same small number repeated (like $97.50) for different items, you're probably reading the RATE column, not the TOTAL

CATEGORIZATION:
- "mitigation": Water extraction, dehumidifier, air mover, air scrubber, HEPA, moisture monitoring, containment, PPE, drying equipment
- "repair": Drywall, paint, flooring, trim, plumbing, electrical, cabinets, demolition, debris removal, cleaning, Overhead, Profit

STEP BY STEP:
1. First, identify the column headers in the table
2. Find the TOTAL or rightmost dollar column
3. For each line item, read the description and the TOTAL amount
4. Categorize as mitigation or repair
5. Sum up the totals to verify they match the document's stated totals

Respond with JSON only (no markdown):
{
  "columnStructure": "Describe the columns you see, e.g., 'Description | Qty | Unit | Labor | Material | Total'",
  "lineItems": [
    {"line": 1, "description": "Full description text", "amount": 375.00, "category": "repair"}
  ],
  "total": 12345.67,
  "mitigationTotal": 1234.56,
  "repairTotal": 11111.11,
  "propertyAddress": "address if visible or null",
  "notes": "Any observations about the parsing"
}

Document: ${fileName}`;

  const content = [
    // Images first
    ...pageImages.map((img) => ({
      type: 'image_url',
      image_url: {
        url: img.image,
        detail: 'high', // High detail for reading text in tables
      },
    })),
    // Then the text prompt
    {
      type: 'text',
      text: userPrompt,
    },
  ];

  // Use only user message (no system message) for better vision compatibility
  const messages = [
    { role: 'user', content },
  ];

  // Validate images before sending
  for (const img of pageImages) {
    if (!img.image || !img.image.startsWith('data:image/')) {
      console.error('[Vision Parse] Invalid image format:', img.image?.substring(0, 50));
      throw new Error('Invalid image format - images must be data URLs');
    }
  }

  // Calculate total request size
  const totalImageSize = pageImages.reduce((sum, img) => sum + img.image.length, 0);
  const estimatedRequestKB = Math.round(totalImageSize / 1024);

  console.log('[Vision Parse] Request details:', {
    model: VISION_MODEL,
    messageCount: messages.length,
    userContentItems: content.length,
    imageCount: pageImages.length,
    totalImageSizeKB: estimatedRequestKB,
    firstImagePrefix: pageImages[0]?.image?.substring(0, 50),
    firstImageLength: pageImages[0]?.image?.length,
  });

  // Retry logic - GPT-4o vision can be flaky
  const maxRetries = 2;
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Vision Parse] Attempt ${attempt}/${maxRetries}`);

      const result = await callOpenAI(messages, {
        model: VISION_MODEL, // gpt-4o
        maxTokens: 4000, // More tokens for detailed extraction
        promptId: 'parseEstimateVision',
      });

      console.log('[Vision Parse] Full raw response:', result.content);

      // Check for truncation
      if (result.truncated) {
        console.error('[Vision Parse] Response was truncated!');
        lastError = new Error('Vision parsing response was truncated - document too large');
        continue;
      }

      // Check if model refused or returned an error message
      const lowerResponse = result.content.toLowerCase();
      if (lowerResponse.startsWith("i'm unable") ||
          lowerResponse.startsWith("i cannot") ||
          lowerResponse.startsWith("sorry") ||
          lowerResponse.includes("unable to process") ||
          lowerResponse.includes("can't see")) {
        console.warn(`[Vision Parse] Model refused on attempt ${attempt}:`, result.content.substring(0, 100));
        lastError = new Error(`Model could not process images: ${result.content.substring(0, 200)}`);
        continue; // Try again
      }

      const parsed = JSON.parse(cleanJsonResponse(result.content));

      console.log(`[Vision Parse] Extracted ${parsed.lineItems?.length || 0} line items`);
      return parsed;
    } catch (error) {
      console.error(`[Vision Parse] Error on attempt ${attempt}:`, error.message);
      lastError = error;
      // Continue to next attempt unless it's a fatal error
      if (error.message?.includes('Rate limit') || error.message?.includes('API error')) {
        break; // Don't retry on rate limits or API errors
      }
    }
  }

  // All retries failed
  console.error('[Vision Parse] All attempts failed:', lastError);
  throw lastError || new Error('Failed to parse document with vision after retries');
};

/**
 * Process a clarification message conversationally
 * This is a HUMAN conversation, not a robot. Be honest, ask questions, confirm before acting.
 *
 * SHARED CONTEXT PRINCIPLE: AI sees everything the user sees.
 * This includes: raw document, parsed items, mitigation rates, O&P, checklist, warnings, etc.
 *
 * @param {Object} currentEstimate - Current estimate state (includes ALL context)
 * @param {Array} conversationHistory - Previous messages in the thread
 * @param {string} userMessage - Latest user message
 * @param {Array} images - Optional array of images {url, storageRef} to include in the message
 * @returns {Promise<Object>} - { message: string, action?: object }
 */
export const processClarification = async (currentEstimate, conversationHistory = [], userMessage, images = []) => {
  const hasImages = images && images.length > 0;
  const model = hasImages ? VISION_MODEL : DEFAULT_MODEL; // Use GPT-4o for vision

  console.log(`[Clarify] Processing with ${model}, images: ${images.length}`);
  // Build a comprehensive summary of EVERYTHING the user can see
  const parsedItems = currentEstimate.upload?.parsedData?.lineItems || [];
  const repairItems = parsedItems.filter(i => i.category === 'repair');
  const mitigationItems = parsedItems.filter(i => i.category === 'mitigation');

  // Mitigation calculated values
  const mitigationValues = currentEstimate.mitigation?.values || {};
  const mitigationRates = currentEstimate.mitigation?.rates || {};
  const mitigationLineItems = currentEstimate.mitigation?.lineItems || [];

  // O&P (Overhead & Profit)
  const opPercentage = currentEstimate.repair?.opPercentage ?? 10;
  const repairSubtotal = currentEstimate.repair?.subtotal || 0;
  const opAmount = repairSubtotal * (opPercentage / 100);

  // Documentation checklist
  const docChecklist = currentEstimate.documentation?.checklist || {};

  // Warnings/issues visible in UI
  const uiWarnings = currentEstimate.warnings || [];

  // Property address
  const propertyAddress = currentEstimate.upload?.parsedData?.propertyAddress ||
                          currentEstimate.propertyAddress ||
                          'Not found in document';

  // Build the raw document section (truncated if very long, but enough for AI to understand)
  const rawText = currentEstimate.upload?.rawText || '';
  const rawTextPreview = rawText.length > 8000
    ? rawText.substring(0, 8000) + '\n\n[... document truncated for length, ' + (rawText.length - 8000) + ' more characters ...]'
    : rawText;

  const currentState = `
CURRENT ESTIMATE STATE (FULL CONTEXT):
══════════════════════════════════════════════════════════════════════

📄 UPLOADED DOCUMENT:
File: ${currentEstimate.upload?.fileName || 'No file uploaded'}
Property Address: ${propertyAddress}

─── RAW DOCUMENT TEXT ───
${rawTextPreview || '(no document text available)'}
─── END RAW DOCUMENT ───

📊 PARSED LINE ITEMS FROM UPLOAD:
- Total parsed: ${parsedItems.length} line items
- Repair items: ${repairItems.length} → $${repairItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString()}
- Mitigation items: ${mitigationItems.length} → $${mitigationItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString()}
- Document's stated totals: Repair $${currentEstimate.upload?.parsedData?.repairTotal?.toLocaleString() || 'N/A'} | Mitigation $${currentEstimate.upload?.parsedData?.mitigationTotal?.toLocaleString() || 'N/A'} | Overall $${currentEstimate.upload?.parsedData?.total?.toLocaleString() || 'N/A'}

REPAIR LINE ITEMS (as parsed):
${repairItems.map((item, i) => `  ${i + 1}. [Line ${item.line}] ${item.description}: $${parseFloat(item.amount || 0).toLocaleString()}`).join('\n') || '  (none parsed)'}

MITIGATION LINE ITEMS (as parsed):
${mitigationItems.map((item, i) => `  ${i + 1}. [Line ${item.line}] ${item.description}: $${parseFloat(item.amount || 0).toLocaleString()}`).join('\n') || '  (none parsed)'}

🔧 CURRENT STEP: ${currentEstimate.currentStep || 1} of 3

💧 MITIGATION SCOPE (User's current values):
- Square Footage: ${mitigationValues.sqft || 0} sqft
- Dehumidifiers: ${mitigationValues.dehumidifiers || 0}
- Air Movers: ${mitigationValues.airMovers || 0}
- Drying Days: ${mitigationValues.dryingDays || 0}
- Contents Manipulation: ${mitigationValues.contentsManipulation ? 'Yes' : 'No'}
- Antimicrobial Treatment: ${mitigationValues.antimicrobial ? 'Yes' : 'No'}
- Category 2/3 Water: ${mitigationValues.cat2or3 ? 'Yes (contaminated water)' : 'No (clean water)'}

📈 MITIGATION RATES ($/unit):
${Object.entries(mitigationRates).map(([key, value]) => `  - ${key}: $${value}`).join('\n') || '  (using defaults)'}

💵 CALCULATED MITIGATION LINE ITEMS:
${mitigationLineItems.length > 0
  ? mitigationLineItems.map((item, i) => `  ${i + 1}. ${item.description}: $${parseFloat(item.amount || 0).toLocaleString()} ${item.unit ? `(${item.unit})` : ''}`).join('\n')
  : '  (no calculated items yet)'}
Mitigation Subtotal: $${(currentEstimate.mitigation?.subtotal || 0).toLocaleString()}

🏗️ REPAIR SCOPE:
Repair Items: ${(currentEstimate.repair?.items || []).length}
Repair Subtotal: $${repairSubtotal.toLocaleString()}
O&P Percentage: ${opPercentage}%
O&P Amount: $${opAmount.toLocaleString()}
Repair Total (with O&P): $${(repairSubtotal + opAmount).toLocaleString()}

📋 DOCUMENTATION CHECKLIST:
${Object.entries(docChecklist).length > 0
  ? Object.entries(docChecklist).map(([item, checked]) => `  ${checked ? '✓' : '○'} ${item}`).join('\n')
  : '  (no checklist items)'}

⚠️ WARNINGS/ISSUES:
${uiWarnings.length > 0
  ? uiWarnings.map(w => `  • ${w}`).join('\n')
  : '  (no warnings)'}

📜 EDIT HISTORY:
${(currentEstimate.history || []).slice(-5).map(h => `  - ${h.timestamp}: ${h.action}`).join('\n') || '  (no history)'}

══════════════════════════════════════════════════════════════════════`;

  const systemPrompt = `You are a helpful assistant working on an insurance estimate. Be direct, honest, and conversational.

CRITICAL RULES:
- NEVER say "Done" or "I've updated" unless you include an ACTION block
- If you CANNOT do something (no action exists), say "I can't do that directly, but here's what you can do..."
- If you include an ACTION block, the action executes immediately - speak in past tense
- Be honest. If something looks wrong or missing, say so directly.
- NEVER claim to have done something you haven't done. This is the #1 rule.

WHEN USER SAYS AN ITEM IS MISSING:
1. SEARCH the RAW DOCUMENT TEXT section for what they mention (furnace, water heater, HVAC, etc.)
2. If you find it in the raw text but NOT in the parsed line items → say "You're right, I see [item] in the raw document at line X but it wasn't captured in parsing. Want me to re-parse or add it manually?"
3. NEVER say "I don't see it" without actually searching the raw document text
4. The raw document is the source of truth - parsed items can be incomplete

WHEN USER POINTS OUT A PROBLEM:
1. Acknowledge what they're saying
2. Look at BOTH the raw document AND parsed items - be honest about what you see
3. Propose a specific solution
4. ASK if they want you to proceed (unless they already said "do it", "go ahead", etc.)

WHEN TAKING ACTION:
Include the ACTION block at the end of your message. The action executes immediately.
CRITICAL: Do NOT use markdown formatting (no ** or __ or backticks) in the action block. Plain text only.

Example response when adding items:
"Done. I've added Overhead ($6,004) and Profit ($6,004) as new repair line items. Your new repair total should be $72,056."

---ACTION---
{"action": "addRepairItems", "items": [{"description": "Overhead", "amount": 6004}, {"description": "Profit", "amount": 6004}]}
---END ACTION---

AVAILABLE ACTIONS:
1. ADD NEW LINE ITEMS (for O&P, missing items, etc.):
   {"action": "addRepairItems", "items": [{"description": "Overhead", "amount": 6004}, {"description": "Profit", "amount": 6004}]}

2. RE-PARSE DOCUMENT (start fresh from raw text):
   {"action": "reparse", "reason": "User wants fresh parse"}

3. MOVE ITEMS BETWEEN CATEGORIES (by line number):
   {"action": "recategorize", "items": [1, 2, 3], "newCategory": "repair"}

4. UPDATE MITIGATION VALUES:
   {"action": "updateMitigation", "field": "sqft", "value": 500}

5. UPDATE EXISTING REPAIR ITEM (Step 3 data):
   {"action": "updateRepair", "itemId": "...", "field": "estimatedAmount", "value": 1000}

6. FIX A PARSED LINE ITEM AMOUNT (Step 1 data - use when user says "that amount is wrong"):
   {"action": "updateParsedItem", "line": 5, "field": "amount", "value": 1443}

IMPORTANT:
- To ADD new items like Overhead and Profit, use "addRepairItems" - NOT "recategorize"
- To FIX an incorrect amount from parsing, use "updateParsedItem" with the line number
- Recategorize only MOVES existing items between categories
- If you cannot find an action that does what the user wants, SAY SO. Do not pretend.

${currentState}`;

  // Build conversation messages
  const messages = [
    { role: 'system', content: systemPrompt },
  ];

  // Add conversation history
  for (const msg of conversationHistory) {
    if (msg.type === 'user') {
      // Check if this history message had images
      if (msg.images && msg.images.length > 0) {
        // Build content array with images for vision
        const content = [
          { type: 'text', text: msg.content || '(image attached)' },
          ...msg.images.map(img => ({
            type: 'image_url',
            image_url: { url: img.url, detail: 'high' },
          })),
        ];
        messages.push({ role: 'user', content });
      } else {
        messages.push({ role: 'user', content: msg.content });
      }
    } else if (msg.type === 'ai') {
      messages.push({ role: 'assistant', content: msg.content });
    }
  }

  // Add current message (with images if provided)
  if (hasImages) {
    // Build content array for vision model
    const userContent = [
      { type: 'text', text: userMessage || 'Please look at the attached image(s).' },
      ...images.map(img => ({
        type: 'image_url',
        image_url: { url: img.url, detail: 'high' },
      })),
    ];
    messages.push({ role: 'user', content: userContent });
    console.log(`[Clarify] Added ${images.length} image(s) to message`);
  } else {
    messages.push({ role: 'user', content: userMessage });
  }

  try {
    const result = await callOpenAI(messages, { model, maxTokens: 1500, promptId: 'processClarification' });
    return extractActionBlock(result.content);
  } catch (error) {
    console.error('Error in clarification:', error);
    return {
      message: "Sorry, I had trouble processing that. Could you try again?",
    };
  }
};

/**
 * Detect trade type and suggest labor split for a repair line item
 * @param {string} description - Line item description
 * @param {number} amount - Line item amount
 * @returns {Promise<Object>} - Trade classification and labor split
 */
export const classifyRepairItem = async (description, amount) => {
  // Try dynamic prompt first
  if (promptContextRef) {
    try {
      return await callPromptWithTracking('classifyRepairItem', {
        description,
        amount,
      });
    } catch (error) {
      console.error('Dynamic prompt failed, using legacy:', error);
    }
  }

  // Legacy fallback using canonical prompt definitions
  const def = getPromptDefinition('classifyRepairItem');
  let userContent = def.userPromptTemplate
    .replace(/{{description}}/g, description)
    .replace(/{{amount}}/g, String(amount));

  const messages = [
    { role: 'system', content: def.systemPrompt },
    { role: 'user', content: userContent },
  ];

  try {
    const result = await callOpenAI(messages, { model: def.model, maxTokens: def.maxTokens, temperature: def.temperature, promptId: 'classifyRepairItem' });
    return JSON.parse(cleanJsonResponse(result.content));
  } catch (error) {
    console.error('Error classifying repair item:', error);
    return {
      trade: 'general',
      laborPct: 50,
      confidence: 0.5,
      reasoning: 'Could not classify - using default',
      shouldSplit: false,
      splitItems: []
    };
  }
};

/**
 * Analyze repair line items for price adjustments
 * Instead of hardcoded multipliers, AI reasons about each item and explains IF/WHY adjustment is warranted
 *
 * @param {Array} lineItems - Array of {description, amount, line} from parsed estimate
 * @param {Object} context - Additional context {propertyAddress, documentNotes, rawText snippet}
 * @returns {Promise<Array>} - Array of analyzed items with recommendations
 */
export const analyzeRepairLineItems = async (lineItems, context = {}) => {
  if (!lineItems || lineItems.length === 0) {
    return [];
  }

  console.log(`[Repair Analysis] Analyzing ${lineItems.length} line items...`);

  const itemsList = lineItems.map((item, idx) =>
    `${idx + 1}. "${item.description}" - $${parseFloat(item.amount || 0).toLocaleString()} (Line ${item.line})`
  ).join('\n');

  const prompt = `You are analyzing repair line items from an insurance estimate to determine if price adjustments are warranted.

CONTEXT:
- These are insurance claim estimates (typically using Xactimate pricing)
- Insurance estimates often use standardized rates that may differ from actual contractor market rates
- Your job is to analyze EACH item and determine:
  1. Is the insurance amount reasonable for this work?
  2. If adjustment is warranted, what's a fair market estimate and WHY?
  3. If NO adjustment needed, say so explicitly

IMPORTANT PRINCIPLES:
- NOT every item needs adjustment - some insurance rates are fair
- Be specific about WHY an adjustment is warranted (labor rates, material costs, complexity)
- Consider the scope of work described, not just the category
- Adjustments should be justified, not arbitrary percentages

PRICING FORMAT REQUIREMENT:
- NEVER use round numbers for recommendedAmount. Real estimates have cents.
- Apply your adjustment percentage to the originalAmount to get a precise number
- Example: $1,234.56 + 13% = $1,395.05 (NOT $1,400.00)
- Example: $847.23 with no adjustment = $847.23 (keep original exactly)

UNIT EXTRACTION:
- If the description mentions quantities (SF, LF, EA, SY, etc.), extract them
- If you can calculate a unit rate, include it
- This makes the final estimate more detailed and credible

PROPERTY: ${context.propertyAddress || 'Not specified'}

LINE ITEMS TO ANALYZE:
${itemsList}

For each item, respond with JSON array (no markdown):
[
  {
    "line": 1,
    "description": "original description",
    "originalAmount": 1234.56,
    "recommendedAmount": 1395.05,
    "adjustmentNeeded": true,
    "adjustmentPct": 13,
    "reasoning": "Specific explanation of why this adjustment is warranted",
    "trade": "drywall",
    "laborPct": 55,
    "confidence": "high",
    "qty": 150,
    "unit": "SF",
    "rate": 9.30
  },
  {
    "line": 2,
    "description": "original description",
    "originalAmount": 500.00,
    "recommendedAmount": 500.00,
    "adjustmentNeeded": false,
    "adjustmentPct": 0,
    "reasoning": "Insurance rate is fair for standard cleaning scope",
    "trade": "cleaning",
    "laborPct": 90,
    "confidence": "high",
    "qty": 1,
    "unit": "EA",
    "rate": 500.00
  }
]

For qty/unit/rate: Extract from description if present. If not clear, use qty=1, unit="LS" (lump sum), rate=recommendedAmount.

Analyze ALL ${lineItems.length} items. Be honest - if insurance pricing is fair, say so.`;

  const messages = [
    { role: 'system', content: 'You are a construction cost estimator. Analyze repair items and provide honest, justified recommendations. Respond only with valid JSON array.' },
    { role: 'user', content: prompt },
  ];

  try {
    const result = await callOpenAI(messages, {
      model: 'gpt-4o-mini',
      maxTokens: 8000, // Increased for large estimates
      promptId: 'analyzeRepairLineItems'
    });

    // Check for truncation on repair analysis too
    if (result.truncated) {
      console.error('[Repair Analysis] Response was truncated - some items may not have been analyzed');
    }

    const analyzed = JSON.parse(cleanJsonResponse(result.content));

    // Validate we got analysis for all items
    if (analyzed.length < lineItems.length) {
      console.warn(`[Repair Analysis] Only ${analyzed.length}/${lineItems.length} items were analyzed - possible truncation`);
    }

    console.log(`[Repair Analysis] Completed. ${analyzed.filter(i => i.adjustmentNeeded).length}/${analyzed.length} items need adjustment`);

    return analyzed;
  } catch (error) {
    console.error('Error analyzing repair items:', error);
    // Return items with no adjustments on error (don't apply arbitrary markups)
    return lineItems.map(item => ({
      line: item.line,
      description: item.description,
      originalAmount: parseFloat(item.amount) || 0,
      recommendedAmount: parseFloat(item.amount) || 0,
      adjustmentNeeded: false,
      adjustmentPct: 0,
      reasoning: 'Analysis unavailable - using original amount',
      trade: 'general',
      laborPct: 50,
      confidence: 'low'
    }));
  }
};

export default {
  initOpenAI,
  generateWOBriefing,
  generateEnhancedWOBriefing,
  buildEnhancedBriefingVars,
  analyzePhoto,
  generateDebugChecklist,
  analyzeWOEfficiency,
  parseEstimateDocument,
  processClarification,
  classifyRepairItem,
  analyzeRepairLineItems,
};
